import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { trackFeatureUse } from '@/lib/activityTracker';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BookOpen,
  Plus,
  FileText,
  Quote,
  Library,
  BarChart3,
  List,
  FolderTree,
  AlertCircle,
  Clock,
  ArrowUpDown,
  Calendar,
  Type,
  Layers,
  Search,
  X,
  Keyboard,
  User,
  ArrowUp,
  ArrowDown,
  Filter,
  Upload,
  Globe,
  StickyNote,
  CheckCircle2,
  CircleDashed,
  Loader2,
  Check,
  Download,
  Copy,
} from 'lucide-react';
import { SHORTCUTS, shouldShowShortcuts, formatShortcut, isMac } from '@/lib/shortcuts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useUIStore } from '@/stores/uiStore';
import { useProjectCitations } from '@/hooks/useProjectCitations';
import { useChapters } from '@/hooks/useChapters';
import { useResearch, ResearchItem } from '@/hooks/useResearch';
import { useProjects } from '@/hooks/useProjects';
import { useCitationPreferences, CitationPreferences } from '@/hooks/useCitationPreferences';
import { CitationListItem } from './CitationListItem';
import { CitationEditDialog } from './CitationEditDialog';
import { AddCitationDialog } from './AddCitationDialog';
import { CitationsByChapter } from './CitationsByChapter';
import { UncitedResearchList } from './UncitedResearchList';
import { generateBibliographyText, generateBibliographyMarkdown, BibliographySource } from '@/lib/bibliography';
import { CitationStyle } from '@/types/project';
import { toast } from '@/hooks/use-toast';
import { CitationSkeleton, SkeletonList } from '@/components/skeletons';

export function CitationsManagerPanel() {
  const { 
    citationsManagerOpen, 
    citationsManagerProjectId, 
    closeCitationsManager,
    editingCitationId,
    setEditingCitation,
  } = useUIStore();
  
  // Persisted preferences
  const { preferences, updatePreference, resetPreferences, isSaving, justSaved } = useCitationPreferences();
  
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  // Chapter filter is not persisted (project-specific)
  const [filterChapter, setFilterChapter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [exportStyle, setExportStyle] = useState<CitationStyle>('APA');
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Destructure preferences for easier access
  const { sortBy, sortDirection, filterSourceType, filterCitationType, filterAssignment } = preferences;
  
  // Track feature use when panel opens
  useEffect(() => {
    if (citationsManagerOpen) {
      trackFeatureUse('citations_manager');
    }
  }, [citationsManagerOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!citationsManagerOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
        
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      
      if (e.key === 'Escape') {
        if (searchQuery) {
          e.preventDefault();
          setSearchQuery('');
          searchInputRef.current?.blur();
        } else {
          closeCitationsManager();
        }
      }
      
      // "D" to toggle direction
      if (e.key === 'd' && !e.ctrlKey && !e.metaKey) {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
        
        e.preventDefault();
        updatePreference('sortDirection', sortDirection === 'asc' ? 'desc' : 'asc');
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [citationsManagerOpen, searchQuery, closeCitationsManager, sortDirection, updatePreference]);
  
  const { citations, isLoading, addCitation, removeCitation, updateCitation, getUniqueSources } = 
    useProjectCitations(citationsManagerProjectId || undefined);
  const { chapters, sections } = useChapters(citationsManagerProjectId || '');
  const { items: researchItems } = useResearch(citationsManagerProjectId || undefined);
  const { projects } = useProjects();
  
  const project = projects.find(p => p.id === citationsManagerProjectId);
  
  // Compute stats
  const stats = useMemo(() => {
    const uniqueSources = getUniqueSources();
    const directQuotes = citations.filter(c => c.is_direct_quote).length;
    const paraphrases = citations.length - directQuotes;
    const unassigned = citations.filter(c => !c.section_id).length;
    
    return {
      total: citations.length,
      uniqueSources: uniqueSources.length,
      directQuotes,
      paraphrases,
      unassigned,
    };
  }, [citations, getUniqueSources]);
  
  // Get uncited research items
  const uncitedItems = useMemo(() => {
    const citedIds = new Set(citations.map(c => c.research_item_id));
    return researchItems.filter(item => 
      !citedIds.has(item.id) && !item.is_authored_by_user
    );
  }, [citations, researchItems]);
  
  // Get all sections flattened
  const allSections = useMemo(() => Object.values(sections).flat(), [sections]);
  
  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filterSourceType) count++;
    if (filterCitationType !== 'all') count++;
    if (filterAssignment !== 'all') count++;
    if (filterChapter) count++;
    return count;
  }, [filterSourceType, filterCitationType, filterAssignment, filterChapter]);
  
  // Filter and sort citations
  const filteredCitations = useMemo(() => {
    let result = [...citations];
    
    // Filter by source type
    if (filterSourceType) {
      result = result.filter(c => c.research_item?.type === filterSourceType);
    }
    
    // Filter by citation type (quote/paraphrase)
    if (filterCitationType === 'quote') {
      result = result.filter(c => c.is_direct_quote);
    } else if (filterCitationType === 'paraphrase') {
      result = result.filter(c => !c.is_direct_quote);
    }
    
    // Filter by assignment status
    if (filterAssignment === 'assigned') {
      result = result.filter(c => c.section_id);
    } else if (filterAssignment === 'unassigned') {
      result = result.filter(c => !c.section_id);
    }
    
    // Filter by chapter
    if (filterChapter) {
      const chapterSectionIds = new Set(
        allSections.filter(s => s.chapter_id === filterChapter).map(s => s.id)
      );
      result = result.filter(c => c.section_id && chapterSectionIds.has(c.section_id));
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c => {
        const sourceAuthor = (c.research_item?.source_author || '').toLowerCase();
        const sourceTitle = (c.research_item?.title || '').toLowerCase();
        const citationText = (c.citation_text || '').toLowerCase();
        const pageRef = (c.page_reference || '').toLowerCase();
        return sourceAuthor.includes(query) || 
               sourceTitle.includes(query) || 
               citationText.includes(query) ||
               pageRef.includes(query);
      });
    }
    
    // Sort based on selected option
    const directionMultiplier = sortDirection === 'asc' ? 1 : -1;
    
    switch (sortBy) {
      case 'manuscript':
        result.sort((a, b) => {
          const sectionA = allSections.find(s => s.id === a.section_id);
          const sectionB = allSections.find(s => s.id === b.section_id);
          const chapterA = sectionA ? chapters.find(c => c.id === sectionA.chapter_id) : null;
          const chapterB = sectionB ? chapters.find(c => c.id === sectionB.chapter_id) : null;
          
          // Unassigned citations go last (or first if desc)
          if (!chapterA && !chapterB) return 0;
          if (!chapterA) return 1 * directionMultiplier;
          if (!chapterB) return -1 * directionMultiplier;
          
          // Sort by chapter order, then section order
          if (chapterA.order_index !== chapterB.order_index) {
            return (chapterA.order_index - chapterB.order_index) * directionMultiplier;
          }
          return ((sectionA?.order_index || 0) - (sectionB?.order_index || 0)) * directionMultiplier;
        });
        break;
        
      case 'author':
        result.sort((a, b) => {
          const authorA = (a.research_item?.source_author || a.research_item?.title || '').toLowerCase();
          const authorB = (b.research_item?.source_author || b.research_item?.title || '').toLowerCase();
          return authorA.localeCompare(authorB) * directionMultiplier;
        });
        break;
        
      case 'date':
        result.sort((a, b) => {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();
          return (dateB - dateA) * directionMultiplier;
        });
        break;
        
      case 'title':
        result.sort((a, b) => {
          const titleA = (a.research_item?.title || '').toLowerCase();
          const titleB = (b.research_item?.title || '').toLowerCase();
          return titleA.localeCompare(titleB) * directionMultiplier;
        });
        break;
        
      case 'type':
        result.sort((a, b) => {
          const typeA = (a.research_item?.type || '').toLowerCase();
          const typeB = (b.research_item?.type || '').toLowerCase();
          return typeA.localeCompare(typeB) * directionMultiplier;
        });
        break;
    }
    
    return result;
  }, [citations, filterSourceType, filterCitationType, filterAssignment, filterChapter, searchQuery, sortBy, sortDirection, chapters, allSections]);
  
  // Recent citations (last 5 by created_at)
  const recentCitations = useMemo(() => {
    return [...citations]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 5);
  }, [citations]);
  
  const editingCitation = editingCitationId 
    ? citations.find(c => c.id === editingCitationId) 
    : null;

  const handleAddCitation = async (
    researchItemId: string,
    sectionId?: string,
    citationText?: string,
    pageReference?: string,
    isDirectQuote?: boolean
  ) => {
    await addCitation(researchItemId, sectionId, citationText, pageReference, isDirectQuote);
    setShowAddDialog(false);
  };

  const handleUpdateCitation = async (
    citationId: string,
    updates: {
      section_id?: string | null;
      citation_text?: string | null;
      page_reference?: string | null;
      is_direct_quote?: boolean;
    }
  ) => {
    await updateCitation(citationId, updates);
    setEditingCitation(null);
  };

  const handleDeleteCitation = async (citationId: string) => {
    await removeCitation(citationId);
  };

  const handleQuickCite = async (researchItem: ResearchItem) => {
    await addCitation(researchItem.id);
  };
  
  const clearAllFilters = () => {
    updatePreference('filterSourceType', null);
    updatePreference('filterCitationType', 'all');
    updatePreference('filterAssignment', 'all');
    setFilterChapter(null);
  };
  
  const getSortDirectionLabel = () => {
    switch (sortBy) {
      case 'manuscript':
        return sortDirection === 'asc' ? 'Start → End' : 'End → Start';
      case 'author':
      case 'title':
      case 'type':
        return sortDirection === 'asc' ? 'A → Z' : 'Z → A';
      case 'date':
        return sortDirection === 'asc' ? 'Newest First' : 'Oldest First';
      default:
        return sortDirection === 'asc' ? 'Ascending' : 'Descending';
    }
  };
  
  // Get unique sources from filtered citations for bibliography
  const getFilteredSources = useCallback((): BibliographySource[] => {
    const uniqueSourceIds = new Set<string>();
    const sources: BibliographySource[] = [];
    
    for (const citation of filteredCitations) {
      const item = citation.research_item;
      if (item && !uniqueSourceIds.has(item.id) && !item.is_authored_by_user) {
        uniqueSourceIds.add(item.id);
        sources.push({
          id: item.id,
          title: item.title,
          type: item.type,
          source_author: item.source_author,
          source_publication: item.source_publication,
          source_publication_date: item.source_publication_date,
          source_url: item.source_url,
          source_doi: item.source_doi,
          source_isbn: item.source_isbn,
          source_page_numbers: item.source_page_numbers,
        });
      }
    }
    
    return sources;
  }, [filteredCitations]);
  
  const handleCopyBibliography = useCallback(() => {
    const sources = getFilteredSources();
    if (sources.length === 0) {
      toast({
        title: "No sources to export",
        description: "There are no citations to export in the current view.",
        variant: "destructive",
      });
      return;
    }
    
    const text = generateBibliographyText(sources, exportStyle);
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: `${sources.length} source${sources.length > 1 ? 's' : ''} copied in ${exportStyle} format.`,
    });
  }, [getFilteredSources, exportStyle]);
  
  const handleDownloadBibliography = useCallback((format: 'txt' | 'md') => {
    const sources = getFilteredSources();
    if (sources.length === 0) {
      toast({
        title: "No sources to export",
        description: "There are no citations to export in the current view.",
        variant: "destructive",
      });
      return;
    }
    
    const content = format === 'md' 
      ? generateBibliographyMarkdown(sources, exportStyle)
      : generateBibliographyText(sources, exportStyle);
    
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bibliography.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Downloaded",
      description: `Bibliography saved as bibliography.${format}`,
    });
  }, [getFilteredSources, exportStyle]);

  return (
    <>
      <Sheet open={citationsManagerOpen} onOpenChange={(open) => !open && closeCitationsManager()}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
          <SheetHeader className="px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Citations Manager
              </SheetTitle>
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <Keyboard className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="w-48">
                      <div className="space-y-2 text-xs">
                        <p className="font-semibold mb-2">Keyboard Shortcuts</p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                          <kbd className="bg-muted px-1.5 py-0.5 rounded font-mono">{SHORTCUTS.citations() || (isMac ? '⌘⇧C' : 'Ctrl+Shift+C')}</kbd>
                          <span>Toggle panel</span>
                          <kbd className="bg-muted px-1.5 py-0.5 rounded font-mono">/</kbd>
                          <span>Focus search</span>
                          <kbd className="bg-muted px-1.5 py-0.5 rounded font-mono">D</kbd>
                          <span>Toggle direction</span>
                          <kbd className="bg-muted px-1.5 py-0.5 rounded font-mono">Esc</kbd>
                          <span>Clear / Close</span>
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {shouldShowShortcuts() && (
                  <Badge variant="outline" className="text-xs">
                    {SHORTCUTS.citations()}
                  </Badge>
                )}
              </div>
            </div>
            {project && (
              <p className="text-sm text-muted-foreground truncate">
                {project.title}
              </p>
            )}
          </SheetHeader>
          
          <ScrollArea className="flex-1">
            <div className="p-6 space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <BarChart3 className="h-4 w-4" />
                    <span className="text-xs font-medium">Total Citations</span>
                  </div>
                  <p className="text-2xl font-semibold">{stats.total}</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Library className="h-4 w-4" />
                    <span className="text-xs font-medium">Unique Sources</span>
                  </div>
                  <p className="text-2xl font-semibold">{stats.uniqueSources}</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Quote className="h-4 w-4" />
                    <span className="text-xs font-medium">Direct Quotes</span>
                  </div>
                  <p className="text-2xl font-semibold">{stats.directQuotes}</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <FileText className="h-4 w-4" />
                    <span className="text-xs font-medium">Paraphrases</span>
                  </div>
                  <p className="text-2xl font-semibold">{stats.paraphrases}</p>
                </div>
              </div>
              
              {/* Recent Citations */}
              {recentCitations.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    Recent Citations
                  </div>
                  <div className="space-y-1.5">
                    {recentCitations.map((citation) => {
                      const allSects = Object.values(sections).flat();
                      const section = allSects.find(s => s.id === citation.section_id);
                      const chapter = section 
                        ? chapters.find(c => c.id === section.chapter_id)
                        : null;
                      
                      return (
                        <div 
                          key={citation.id}
                          className="flex items-center gap-2 p-2 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer text-sm"
                          onClick={() => setEditingCitation(citation.id)}
                        >
                          <Quote className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="truncate font-medium">
                              {citation.research_item?.source_author || citation.research_item?.title || 'Unknown source'}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {section ? `${chapter?.title || 'Chapter'} › ${section.title}` : 'Unassigned'}
                            </p>
                          </div>
                          {citation.is_direct_quote && (
                            <Badge variant="secondary" className="text-[10px] h-5 shrink-0">Quote</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Add Citation Button */}
              <Button 
                onClick={() => setShowAddDialog(true)} 
                className="w-full gap-2"
                disabled={researchItems.length === 0}
              >
                <Plus className="h-4 w-4" />
                Add Citation
              </Button>
              
              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full grid grid-cols-3">
                  <TabsTrigger value="all" className="gap-1.5">
                    <List className="h-3.5 w-3.5" />
                    All
                  </TabsTrigger>
                  <TabsTrigger value="chapters" className="gap-1.5">
                    <FolderTree className="h-3.5 w-3.5" />
                    By Chapter
                  </TabsTrigger>
                  <TabsTrigger value="uncited" className="gap-1.5 relative">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Uncited
                    {uncitedItems.length > 0 && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-amber-500 text-[10px] text-white flex items-center justify-center">
                        {uncitedItems.length}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>
                
                {/* All Citations Tab */}
                <TabsContent value="all" className="mt-4 space-y-3">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by source, title, or citation text..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-9 pr-12"
                      ref={searchInputRef}
                    />
                    {searchQuery ? (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono">/</kbd>
                    )}
                  </div>
                  
                  {/* Sort and Filter Controls */}
                  <div className="flex items-center gap-2">
                    {/* Sort Dropdown */}
                    <Select value={sortBy} onValueChange={(v) => updatePreference('sortBy', v as CitationPreferences['sortBy'])}>
                      <SelectTrigger className="w-[140px] h-8 text-xs">
                        <ArrowUpDown className="h-3 w-3 mr-1.5" />
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manuscript">
                          <div className="flex items-center gap-2">
                            <Layers className="h-3.5 w-3.5" />
                            Manuscript Order
                          </div>
                        </SelectItem>
                        <SelectItem value="author">
                          <div className="flex items-center gap-2">
                            <User className="h-3.5 w-3.5" />
                            Author Name
                          </div>
                        </SelectItem>
                        <SelectItem value="date">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3.5 w-3.5" />
                            Date Added
                          </div>
                        </SelectItem>
                        <SelectItem value="title">
                          <div className="flex items-center gap-2">
                            <Type className="h-3.5 w-3.5" />
                            Source Title
                          </div>
                        </SelectItem>
                        <SelectItem value="type">
                          <div className="flex items-center gap-2">
                            <FolderTree className="h-3.5 w-3.5" />
                            Source Type
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Direction Toggle */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2"
                            onClick={() => updatePreference('sortDirection', sortDirection === 'asc' ? 'desc' : 'asc')}
                          >
                            {sortDirection === 'asc' ? (
                              <ArrowUp className="h-3.5 w-3.5" />
                            ) : (
                              <ArrowDown className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          <p className="text-xs">{getSortDirectionLabel()}</p>
                          <p className="text-xs text-muted-foreground">Press D to toggle</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    {/* Filters Popover */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 px-2.5 gap-1.5">
                          <Filter className="h-3.5 w-3.5" />
                          Filters
                          {activeFilterCount > 0 && (
                            <Badge variant="secondary" className="h-4 w-4 p-0 text-[10px] flex items-center justify-center">
                              {activeFilterCount}
                            </Badge>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-3" align="start">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium">Filters</h4>
                            {activeFilterCount > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={clearAllFilters}
                              >
                                Clear all
                              </Button>
                            )}
                          </div>
                          
                          {/* Citation Type */}
                          <div className="space-y-2">
                            <label className="text-xs font-medium text-muted-foreground">Citation Type</label>
                            <div className="flex flex-wrap gap-1.5">
                              <Badge
                                variant={filterCitationType === 'all' ? 'default' : 'outline'}
                                className="cursor-pointer text-xs"
                                onClick={() => updatePreference('filterCitationType', 'all')}
                              >
                                All
                              </Badge>
                              <Badge
                                variant={filterCitationType === 'quote' ? 'default' : 'outline'}
                                className="cursor-pointer text-xs"
                                onClick={() => updatePreference('filterCitationType', 'quote')}
                              >
                                <Quote className="h-3 w-3 mr-1" />
                                Quotes
                              </Badge>
                              <Badge
                                variant={filterCitationType === 'paraphrase' ? 'default' : 'outline'}
                                className="cursor-pointer text-xs"
                                onClick={() => updatePreference('filterCitationType', 'paraphrase')}
                              >
                                <FileText className="h-3 w-3 mr-1" />
                                Paraphrases
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Assignment Status */}
                          <div className="space-y-2">
                            <label className="text-xs font-medium text-muted-foreground">Assignment</label>
                            <div className="flex flex-wrap gap-1.5">
                              <Badge
                                variant={filterAssignment === 'all' ? 'default' : 'outline'}
                                className="cursor-pointer text-xs"
                                onClick={() => updatePreference('filterAssignment', 'all')}
                              >
                                All
                              </Badge>
                              <Badge
                                variant={filterAssignment === 'assigned' ? 'default' : 'outline'}
                                className="cursor-pointer text-xs"
                                onClick={() => updatePreference('filterAssignment', 'assigned')}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Assigned
                              </Badge>
                              <Badge
                                variant={filterAssignment === 'unassigned' ? 'default' : 'outline'}
                                className="cursor-pointer text-xs"
                                onClick={() => updatePreference('filterAssignment', 'unassigned')}
                              >
                                <CircleDashed className="h-3 w-3 mr-1" />
                                Unassigned
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Source Type */}
                          <div className="space-y-2">
                            <label className="text-xs font-medium text-muted-foreground">Source Type</label>
                            <div className="flex flex-wrap gap-1.5">
                              <Badge
                                variant={filterSourceType === null ? 'default' : 'outline'}
                                className="cursor-pointer text-xs"
                                onClick={() => updatePreference('filterSourceType', null)}
                              >
                                All
                              </Badge>
                              <Badge
                                variant={filterSourceType === 'upload' ? 'default' : 'outline'}
                                className="cursor-pointer text-xs"
                                onClick={() => updatePreference('filterSourceType', 'upload')}
                              >
                                <Upload className="h-3 w-3 mr-1" />
                                Uploads
                              </Badge>
                              <Badge
                                variant={filterSourceType === 'note' ? 'default' : 'outline'}
                                className="cursor-pointer text-xs"
                                onClick={() => updatePreference('filterSourceType', 'note')}
                              >
                                <StickyNote className="h-3 w-3 mr-1" />
                                Notes
                              </Badge>
                              <Badge
                                variant={filterSourceType === 'web_snapshot' ? 'default' : 'outline'}
                                className="cursor-pointer text-xs"
                                onClick={() => updatePreference('filterSourceType', 'web_snapshot')}
                              >
                                <Globe className="h-3 w-3 mr-1" />
                                Web
                              </Badge>
                              <Badge
                                variant={filterSourceType === 'quote' ? 'default' : 'outline'}
                                className="cursor-pointer text-xs"
                                onClick={() => updatePreference('filterSourceType', 'quote')}
                              >
                                <Quote className="h-3 w-3 mr-1" />
                                Quotes
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Chapter Filter */}
                          {chapters.length > 0 && (
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-muted-foreground">Chapter</label>
                              <Select value={filterChapter || 'all'} onValueChange={(v) => setFilterChapter(v === 'all' ? null : v)}>
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="All chapters" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">All chapters</SelectItem>
                                  {chapters.map((chapter) => (
                                    <SelectItem key={chapter.id} value={chapter.id}>
                                      {chapter.title}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                    
                    {/* Saving/Saved Indicator */}
                    {isSaving && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground animate-in fade-in duration-200">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        <span>Saving...</span>
                      </div>
                    )}
                    {!isSaving && justSaved && (
                      <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-500 animate-in fade-in duration-200">
                        <Check className="h-3 w-3" />
                        <span>Saved</span>
                      </div>
                    )}
                    
                    {/* Export Button - pushed to the right */}
                    <div className="flex-1" />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 px-2.5 gap-1.5">
                          <Download className="h-3.5 w-3.5" />
                          Export
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel className="text-xs">Citation Style</DropdownMenuLabel>
                        <Select value={exportStyle} onValueChange={(v) => setExportStyle(v as CitationStyle)}>
                          <SelectTrigger className="h-8 text-xs mx-2 mb-2 w-[calc(100%-1rem)]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="APA">APA (7th ed)</SelectItem>
                            <SelectItem value="MLA">MLA (9th ed)</SelectItem>
                            <SelectItem value="Chicago-Notes">Chicago Notes</SelectItem>
                            <SelectItem value="Chicago-Author">Chicago Author-Date</SelectItem>
                            <SelectItem value="Harvard">Harvard</SelectItem>
                            <SelectItem value="IEEE">IEEE</SelectItem>
                            <SelectItem value="Turabian">Turabian</SelectItem>
                            <SelectItem value="Vancouver">Vancouver</SelectItem>
                            <SelectItem value="AMA">AMA</SelectItem>
                          </SelectContent>
                        </Select>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleCopyBibliography}>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy to clipboard
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownloadBibliography('txt')}>
                          <Download className="h-4 w-4 mr-2" />
                          Download as .txt
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownloadBibliography('md')}>
                          <FileText className="h-4 w-4 mr-2" />
                          Download as .md
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <div className="px-2 py-1.5 text-xs text-muted-foreground">
                          {filteredCitations.length} citation{filteredCitations.length !== 1 ? 's' : ''} selected
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  {/* Active Filters Display */}
                  {activeFilterCount > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {filterCitationType !== 'all' && (
                        <Badge variant="secondary" className="gap-1 text-xs">
                          {filterCitationType === 'quote' ? 'Quotes' : 'Paraphrases'}
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-destructive"
                            onClick={() => updatePreference('filterCitationType', 'all')}
                          />
                        </Badge>
                      )}
                      {filterAssignment !== 'all' && (
                        <Badge variant="secondary" className="gap-1 text-xs">
                          {filterAssignment === 'assigned' ? 'Assigned' : 'Unassigned'}
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-destructive"
                            onClick={() => updatePreference('filterAssignment', 'all')}
                          />
                        </Badge>
                      )}
                      {filterSourceType && (
                        <Badge variant="secondary" className="gap-1 text-xs capitalize">
                          {filterSourceType.replace('_', ' ')}
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-destructive"
                            onClick={() => updatePreference('filterSourceType', null)}
                          />
                        </Badge>
                      )}
                      {filterChapter && (
                        <Badge variant="secondary" className="gap-1 text-xs">
                          {chapters.find(c => c.id === filterChapter)?.title || 'Chapter'}
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-destructive"
                            onClick={() => setFilterChapter(null)}
                          />
                        </Badge>
                      )}
                    </div>
                  )}
                  
                  {/* Citations List */}
                  {isLoading ? (
                    <SkeletonList count={4} Component={CitationSkeleton} />
                  ) : filteredCitations.length === 0 ? (
                    <div className="py-8 text-center">
                      <BookOpen className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                      <p className="text-muted-foreground">
                        {activeFilterCount > 0 || searchQuery ? 'No matching citations' : 'No citations yet'}
                      </p>
                      <p className="text-sm text-muted-foreground/70">
                        {activeFilterCount > 0 || searchQuery 
                          ? 'Try adjusting your filters or search'
                          : 'Add citations from your research library'
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredCitations.map((citation) => (
                        <CitationListItem
                          key={citation.id}
                          citation={citation}
                          chapters={chapters}
                          sections={sections}
                          onEdit={() => setEditingCitation(citation.id)}
                          onDelete={() => handleDeleteCitation(citation.id)}
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>
                
                {/* By Chapter Tab */}
                <TabsContent value="chapters" className="mt-4">
                  <CitationsByChapter
                    citations={citations}
                    chapters={chapters}
                    sections={sections}
                    projectId={citationsManagerProjectId || ''}
                  />
                </TabsContent>
                
                {/* Uncited Tab */}
                <TabsContent value="uncited" className="mt-4">
                  <UncitedResearchList
                    items={uncitedItems}
                    onQuickCite={handleQuickCite}
                    onAddWithDetails={() => {
                      setShowAddDialog(true);
                    }}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </ScrollArea>
          
          {/* Footer Actions */}
          <div className="border-t p-4 flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => {
                closeCitationsManager();
                window.location.href = '/research';
              }}
            >
              <Library className="h-4 w-4 mr-1.5" />
              Research Library
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      
      {/* Add Citation Dialog */}
      <AddCitationDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        researchItems={researchItems}
        chapters={chapters}
        sections={sections}
        onAdd={handleAddCitation}
      />
      
      {/* Edit Citation Dialog */}
      {editingCitation && (
        <CitationEditDialog
          isOpen={!!editingCitation}
          onClose={() => setEditingCitation(null)}
          citation={editingCitation}
          chapters={chapters}
          sections={sections}
          onSave={handleUpdateCitation}
        />
      )}
    </>
  );
}
